// The view a virtual grid follows (`VirtualMasonry`, `VirtualRows`): the part of it under the
// scroller's view, and its width.
import { useCallback, useEffect, useState } from "react";

/** Cards are kept mounted this many screens above and below the view. */
const OVERSCAN_SCREENS = 1;
/** The view is followed in steps of a quarter screen, so a scroll re-renders a few times a
 *  screen, never on every frame. */
const STEPS_A_SCREEN = 4;

/** The part of the grid to keep mounted, in the grid's own pixels. */
export interface View {
  top: number;
  bottom: number;
}

/** No layout to follow: the scroller has no height (jsdom, or hidden). */
export const NO_LAYOUT = "none";

/**
 * The grid's place under the view of `root`, followed as it scrolls or resizes; null until it is
 * first read (a frame after the grid mounts), `NO_LAYOUT` where there is none to read.
 */
export function useView(root: HTMLElement | null, grid: HTMLElement | null): View | typeof NO_LAYOUT | null {
  const [view, setView] = useState<View | typeof NO_LAYOUT | null>(null);
  useEffect(() => {
    if (root === null || grid === null) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const screen = root.clientHeight;
      if (screen <= 0) {
        setView(NO_LAYOUT);
        return;
      }
      const offset = grid.getBoundingClientRect().top - root.getBoundingClientRect().top;
      const step = screen / STEPS_A_SCREEN;
      const top = Math.floor((-offset - screen * OVERSCAN_SCREENS) / step) * step;
      const bottom = Math.ceil((-offset + screen * (1 + OVERSCAN_SCREENS)) / step) * step;
      setView((shown) =>
        shown !== null && shown !== NO_LAYOUT && shown.top === top && shown.bottom === bottom
          ? shown
          : { top, bottom },
      );
    };
    const follow = () => {
      if (frame === 0) frame = requestAnimationFrame(measure);
    };
    follow();
    root.addEventListener("scroll", follow, { passive: true });
    const resized = new ResizeObserver(follow);
    resized.observe(root);
    return () => {
      root.removeEventListener("scroll", follow);
      resized.disconnect();
      if (frame !== 0) cancelAnimationFrame(frame);
    };
  }, [root, grid]);
  return view;
}

/**
 * The grid, and its width: read as it mounts (its ref, before the browser paints, so where
 * nothing lays out, jsdom's 0, every card is in the flow at once, fake timers or not) and again
 * as it resizes (the panel opening); null until it mounts.
 */
export function useGrid(): [HTMLDivElement | null, (element: HTMLDivElement | null) => void, number | null] {
  const [grid, setGrid] = useState<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  const attach = useCallback((element: HTMLDivElement | null) => {
    setGrid(element);
    if (element !== null) setWidth(element.clientWidth);
  }, []);
  useEffect(() => {
    if (grid === null) return;
    const resized = new ResizeObserver(() => setWidth(grid.clientWidth));
    resized.observe(grid);
    return () => resized.disconnect();
  }, [grid]);
  return [grid, attach, width];
}
